import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Trash2, Edit, Plus, LogIn, X, LogOut, Upload, BarChart3, Link2, Eye, Users, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  // Simple auth - store username and password directly
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [storedAuth, setStoredAuth] = useState({ username: "", password: "" });
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [activeTab, setActiveTab] = useState("albums");

  // Data states
  const [albums, setAlbums] = useState([]);
  const [videos, setVideos] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [news, setNews] = useState([]);
  const [comments, setComments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [about, setAbout] = useState({ title: "About Me", content: "", photo_url: "" });
  const [settings, setSettings] = useState({
    bg_body: "#000000",
    bg_container: "#0a1628",
    bg_section: "#0d1f3c",
    accent_orange: "#ff6600",
    accent_cyan: "#00ddff",
    text_white: "#ffffff",
    text_link: "#66ccff",
    border_color: "#1a4a7a",
    font_heading: "Verdana",
    font_body: "Verdana",
    font_nav: "Verdana",
    bg_image: ""
  });
  
  // New data states for socials and analytics
  const [socials, setSocials] = useState([]);
  const [analytics, setAnalytics] = useState({ total_views: 0, unique_visitors: 0, views_today: 0, views_this_week: 0, popular_pages: [], recent_views: [] });
  const [dailyStats, setDailyStats] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [secretPage, setSecretPage] = useState({ content: "", song_url: "/admiration.mp3", bg_image: "" });
  const [homepageSettings, setHomepageSettings] = useState({ 
    sections: [
      { key: "latest_video", title: "Latest Video", visible: true, order: 0, colors: { bg: "", text: "", border: "", accent: "" } },
      { key: "blogs", title: "Latest Blog Entries", visible: true, order: 1, colors: { bg: "", text: "", border: "", accent: "" } },
      { key: "socials", title: "Socials", visible: true, order: 2, colors: { bg: "", text: "", border: "", accent: "" } },
      { key: "music", title: ":: Music :: Choose Your Album ::", visible: true, order: 3, colors: { bg: "", text: "", border: "", accent: "" } },
      { key: "news", title: ":: News & Updates ::", visible: true, order: 4, colors: { bg: "", text: "", border: "", accent: "" } },
      { key: "comments", title: ":: Leave a Comment ::", visible: true, order: 5, colors: { bg: "", text: "", border: "", accent: "" } },
      { key: "badges", title: "", visible: true, order: 6, colors: { bg: "", text: "", border: "", accent: "" } },
    ],
    badges: [{ url: "https://nezuai.com/#badge", image_url: "https://nezuai.com/88x31_classic.png", alt: "nezuai.com" }]
  });

  // Form states
  const [albumForm, setAlbumForm] = useState({ title: "", cover_url: "", bandcamp_url: "", release_date: "", order: 0 });
  const [videoForm, setVideoForm] = useState({ title: "", youtube_id: "", category: "recommended", is_featured: false });
  const [blogForm, setBlogForm] = useState({ title: "", content: "", excerpt: "" });
  const [newsForm, setNewsForm] = useState({ content: "" });
  const [photoForm, setPhotoForm] = useState({ title: "", description: "", post_type: "single", images: [""] });
  const [socialForm, setSocialForm] = useState({ platform: "youtube", url: "", display_name: "", order: 0, is_active: true });

  // Edit states
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editingSocial, setEditingSocial] = useState(null);

  // Modal states
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', id: null, name: '' });
  
  // File upload ref
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Check for stored auth on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('kezzio_user');
    const savedPass = localStorage.getItem('kezzio_pass');
    if (savedUser && savedPass) {
      setStoredAuth({ username: savedUser, password: savedPass });
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  // Helper to make authenticated requests - always read fresh from localStorage
  const authFetch = async (url, options = {}) => {
    const username = localStorage.getItem('kezzio_user') || '';
    const password = localStorage.getItem('kezzio_pass') || '';
    const auth = "Basic " + window.btoa(username + ":" + password);
    console.log("authFetch sending to:", url, "with auth user:", username);
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        "Authorization": auth,
      },
    });
  };

  const handleLogin = async () => {
    const auth = "Basic " + window.btoa(credentials.username + ":" + credentials.password);
    console.log("Login attempt with user:", credentials.username);
    
    try {
      // Test against a protected endpoint to verify credentials
      const res = await fetch(`${API}/socials/all`, {
        credentials: 'include',
        headers: { "Authorization": auth }
      });
      
      if (res.ok) {
        localStorage.setItem('kezzio_user', credentials.username);
        localStorage.setItem('kezzio_pass', credentials.password);
        setStoredAuth({ username: credentials.username, password: credentials.password });
        setIsAuthenticated(true);
        toast.success("Logged in!");
      } else {
        toast.error("aye twin this aint the proper login get up outta here!");
      }
    } catch (e) {
      console.log("Login error:", e);
      toast.error("aye twin this aint the proper login get up outta here!");
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('kezzio_user');
    localStorage.removeItem('kezzio_pass');
    setStoredAuth({ username: "", password: "" });
    setIsAuthenticated(false);
  };

  const fetchAllData = async () => {
    try {
      const [albumsRes, videosRes, blogsRes, newsRes, commentsRes, photosRes, aboutRes, settingsRes, socialsRes, analyticsRes, uploadsRes, secretRes, homepageRes] = await Promise.all([
        fetch(`${API}/albums`),
        fetch(`${API}/videos`),
        fetch(`${API}/blogs`),
        fetch(`${API}/news`),
        fetch(`${API}/comments`),
        fetch(`${API}/photos`),
        fetch(`${API}/about`),
        fetch(`${API}/settings`),
        authFetch(`${API}/socials/all`),
        authFetch(`${API}/analytics/summary`),
        authFetch(`${API}/uploads`),
        fetch(`${API}/secret`),
        fetch(`${API}/homepage`),
      ]);
      
      if (albumsRes.ok) setAlbums(await albumsRes.json());
      if (videosRes.ok) setVideos(await videosRes.json());
      if (blogsRes.ok) setBlogs(await blogsRes.json());
      if (newsRes.ok) setNews(await newsRes.json());
      if (commentsRes.ok) setComments(await commentsRes.json());
      if (photosRes.ok) setPhotos(await photosRes.json());
      if (aboutRes.ok) setAbout(await aboutRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (socialsRes.ok) setSocials(await socialsRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (uploadsRes.ok) setUploads(await uploadsRes.json());
      if (secretRes.ok) setSecretPage(await secretRes.json());
      if (homepageRes.ok) setHomepageSettings(await homepageRes.json());
      
      // Fetch daily stats separately
      const dailyRes = await authFetch(`${API}/analytics/daily`);
      if (dailyRes.ok) setDailyStats(await dailyRes.json());
    } catch (e) {
      toast.error("Error fetching data");
    }
  };

  // ========== CRUD HANDLERS ==========
  
  const handleAlbumSubmit = async () => {
    if (!albumForm.title || !albumForm.cover_url || !albumForm.bandcamp_url) {
      toast.error("Please fill in title, cover URL, and Bandcamp URL");
      return;
    }
    try {
      const method = editingAlbum ? "PUT" : "POST";
      const url = editingAlbum ? `${API}/albums/${editingAlbum.id}` : `${API}/albums`;
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(albumForm),
      });
      if (res.ok) {
        toast.success(editingAlbum ? "Album updated!" : "Album created!");
        setShowAlbumModal(false);
        setEditingAlbum(null);
        setAlbumForm({ title: "", cover_url: "", bandcamp_url: "", release_date: "", order: 0 });
        fetchAllData();
      } else {
        toast.error("Failed to save album");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    }
  };

  const deleteAlbum = async (id) => {
    try {
      const res = await authFetch(`${API}/albums/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Deleted!");
        fetchAllData();
      } else {
        console.log("Delete album failed:", res.status);
        toast.error("Failed to delete: " + res.status);
      }
    } catch (e) {
      console.log("Delete album error:", e);
      toast.error("Error deleting");
    }
    setDeleteConfirm({ show: false, type: '', id: null, name: '' });
  };
  
  const confirmDelete = (type, id, name) => {
    setDeleteConfirm({ show: true, type, id, name });
  };
  
  const executeDelete = async () => {
    const { type, id } = deleteConfirm;
    if (type === 'album') await deleteAlbum(id);
    else if (type === 'video') await deleteVideo(id);
    else if (type === 'blog') await deleteBlog(id);
    else if (type === 'news') await deleteNews(id);
    else if (type === 'comment') await deleteComment(id);
    else if (type === 'photo') await deletePhoto(id);
    else if (type === 'social') await deleteSocial(id);
    else if (type === 'upload') await deleteUpload(id);
  };

  const handleVideoSubmit = async () => {
    if (!videoForm.title || !videoForm.youtube_id) {
      toast.error("Please fill in title and YouTube ID");
      return;
    }
    try {
      const method = editingVideo ? "PUT" : "POST";
      const url = editingVideo ? `${API}/videos/${editingVideo.id}` : `${API}/videos`;
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(videoForm),
      });
      if (res.ok) {
        toast.success(editingVideo ? "Video updated!" : "Video created!");
        setShowVideoModal(false);
        setEditingVideo(null);
        setVideoForm({ title: "", youtube_id: "", category: "recommended", is_featured: false });
        fetchAllData();
      } else {
        toast.error("Failed to save video");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    }
  };

  const deleteVideo = async (id) => {
    try {
      const res = await authFetch(`${API}/videos/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Deleted!");
        fetchAllData();
      } else {
        console.log("Delete video failed:", res.status);
        toast.error("Failed to delete: " + res.status);
      }
    } catch (e) {
      console.log("Delete video error:", e);
      toast.error("Error deleting");
    }
    setDeleteConfirm({ show: false, type: '', id: null, name: '' });
  };

  const handleBlogSubmit = async () => {
    if (!blogForm.title || !blogForm.content) {
      toast.error("Please fill in title and content");
      return;
    }
    try {
      const method = editingBlog ? "PUT" : "POST";
      const url = editingBlog ? `${API}/blogs/${editingBlog.id}` : `${API}/blogs`;
      console.log("Blog submit - URL:", url, "Method:", method);
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(blogForm),
      });
      console.log("Blog submit response status:", res.status);
      if (res.ok) {
        toast.success(editingBlog ? "Blog updated!" : "Blog created!");
        setShowBlogModal(false);
        setEditingBlog(null);
        setBlogForm({ title: "", content: "", excerpt: "" });
        fetchAllData();
      } else {
        const errText = await res.text();
        console.log("Blog submit error:", res.status, errText);
        toast.error("Failed to save blog: " + res.status);
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    }
  };

  const deleteBlog = async (id) => {
    try {
      const res = await authFetch(`${API}/blogs/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Deleted!");
        fetchAllData();
      } else {
        console.log("Delete blog failed:", res.status);
        toast.error("Failed to delete: " + res.status);
      }
    } catch (e) {
      console.log("Delete blog error:", e);
      toast.error("Error deleting");
    }
    setDeleteConfirm({ show: false, type: '', id: null, name: '' });
  };

  const handleNewsSubmit = async () => {
    if (!newsForm.content) {
      toast.error("Please enter news content");
      return;
    }
    try {
      const method = editingNews ? "PUT" : "POST";
      const url = editingNews ? `${API}/news/${editingNews.id}` : `${API}/news`;
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newsForm),
      });
      if (res.ok) {
        toast.success(editingNews ? "News updated!" : "News created!");
        setShowNewsModal(false);
        setEditingNews(null);
        setNewsForm({ content: "" });
        fetchAllData();
      } else {
        toast.error("Failed to save news");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    }
  };

  const deleteNews = async (id) => {
    try {
      const res = await authFetch(`${API}/news/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Deleted!");
        fetchAllData();
      } else {
        console.log("Delete news failed:", res.status);
        toast.error("Failed to delete: " + res.status);
      }
    } catch (e) {
      console.log("Delete news error:", e);
      toast.error("Error deleting");
    }
    setDeleteConfirm({ show: false, type: '', id: null, name: '' });
  };

  const deleteComment = async (id) => {
    try {
      const res = await authFetch(`${API}/comments/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Deleted!");
        fetchAllData();
      } else {
        console.log("Delete comment failed:", res.status);
        toast.error("Failed to delete: " + res.status);
      }
    } catch (e) {
      console.log("Delete comment error:", e);
      toast.error("Error deleting");
    }
    setDeleteConfirm({ show: false, type: '', id: null, name: '' });
  };

  const handlePhotoSubmit = async () => {
    if (!photoForm.title) {
      toast.error("Please enter a title");
      return;
    }
    const filteredImages = photoForm.images.filter(img => img.trim() !== "");
    if (filteredImages.length === 0) {
      toast.error("Please add at least one image URL");
      return;
    }
    try {
      const method = editingPhoto ? "PUT" : "POST";
      const url = editingPhoto ? `${API}/photos/${editingPhoto.id}` : `${API}/photos`;
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...photoForm, images: filteredImages }),
      });
      if (res.ok) {
        toast.success(editingPhoto ? "Photo updated!" : "Photo created!");
        setShowPhotoModal(false);
        setEditingPhoto(null);
        setPhotoForm({ title: "", description: "", post_type: "single", images: [""] });
        fetchAllData();
      } else {
        toast.error("Failed to save photo");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    }
  };

  const deletePhoto = async (id) => {
    try {
      const res = await authFetch(`${API}/photos/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Deleted!");
        fetchAllData();
      } else {
        console.log("Delete photo failed:", res.status);
        toast.error("Failed to delete: " + res.status);
      }
    } catch (e) {
      console.log("Delete photo error:", e);
      toast.error("Error deleting");
    }
    setDeleteConfirm({ show: false, type: '', id: null, name: '' });
  };

  const addImageField = () => {
    setPhotoForm({ ...photoForm, images: [...photoForm.images, ""] });
  };

  const updateImageField = (index, value) => {
    const newImages = [...photoForm.images];
    newImages[index] = value;
    setPhotoForm({ ...photoForm, images: newImages });
  };

  const removeImageField = (index) => {
    const newImages = photoForm.images.filter((_, i) => i !== index);
    setPhotoForm({ ...photoForm, images: newImages.length > 0 ? newImages : [""] });
  };

  const handleAboutSubmit = async () => {
    try {
      const res = await authFetch(`${API}/about`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(about),
      });
      if (res.ok) {
        toast.success("About page updated!");
        fetchAllData();
      } else {
        toast.error("Failed to save");
      }
    } catch (e) {
      toast.error("Error saving");
    }
  };

  const handleSettingsSubmit = async () => {
    try {
      const res = await authFetch(`${API}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        toast.success("Settings saved! Refresh to see changes.");
        fetchAllData();
      } else {
        toast.error("Failed to save");
      }
    } catch (e) {
      toast.error("Error saving");
    }
  };

  // ========== SOCIAL LINKS HANDLERS ==========
  
  const handleSocialSubmit = async () => {
    if (!socialForm.platform || !socialForm.url || !socialForm.display_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const method = editingSocial ? "PUT" : "POST";
      const url = editingSocial ? `${API}/socials/${editingSocial.id}` : `${API}/socials`;
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(socialForm),
      });
      if (res.ok) {
        toast.success(editingSocial ? "Social link updated!" : "Social link created!");
        setShowSocialModal(false);
        setEditingSocial(null);
        setSocialForm({ platform: "youtube", url: "", display_name: "", order: 0, is_active: true });
        fetchAllData();
      } else {
        toast.error("Failed to save social link");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    }
  };

  const deleteSocial = async (id) => {
    try {
      const res = await authFetch(`${API}/socials/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Deleted!");
        fetchAllData();
      } else {
        toast.error("Failed to delete");
      }
    } catch (e) {
      toast.error("Error deleting");
    }
    setDeleteConfirm({ show: false, type: '', id: null, name: '' });
  };

  // ========== IMAGE UPLOAD HANDLERS ==========
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await authFetch(`${API}/upload`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Uploaded! URL: ${API.replace('/api', '')}${data.url}`);
        fetchAllData();
        // Copy URL to clipboard
        navigator.clipboard.writeText(`${API.replace('/api', '')}${data.url}`);
        toast.success("URL copied to clipboard!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Upload failed");
      }
    } catch (e) {
      toast.error("Upload error: " + e.message);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const deleteUpload = async (filename) => {
    try {
      const res = await authFetch(`${API}/uploads/${filename}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("File deleted!");
        fetchAllData();
      } else {
        toast.error("Failed to delete");
      }
    } catch (e) {
      toast.error("Error deleting");
    }
    setDeleteConfirm({ show: false, type: '', id: null, name: '' });
  };

  // ========== SECRET PAGE HANDLER ==========
  
  const handleSecretSubmit = async () => {
    try {
      const res = await authFetch(`${API}/secret`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(secretPage),
      });
      if (res.ok) {
        toast.success("Secret page saved!");
        fetchAllData();
      } else {
        toast.error("Failed to save");
      }
    } catch (e) {
      toast.error("Error saving");
    }
  };

  // ========== HOMEPAGE SETTINGS HANDLERS ==========
  
  const handleHomepageSubmit = async () => {
    try {
      const res = await authFetch(`${API}/homepage`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(homepageSettings),
      });
      if (res.ok) {
        toast.success("Homepage settings saved!");
        fetchAllData();
      } else {
        toast.error("Failed to save");
      }
    } catch (e) {
      toast.error("Error saving");
    }
  };

  const updateSection = (key, field, value) => {
    setHomepageSettings(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.key === key ? { ...s, [field]: value } : s
      )
    }));
  };

  const updateSectionColor = (key, colorField, value) => {
    setHomepageSettings(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.key === key ? { ...s, colors: { ...s.colors, [colorField]: value } } : s
      )
    }));
  };

  const moveSection = (key, direction) => {
    const sections = [...homepageSettings.sections].sort((a, b) => a.order - b.order);
    const index = sections.findIndex(s => s.key === key);
    if ((direction === 'up' && index > 0) || (direction === 'down' && index < sections.length - 1)) {
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      const tempOrder = sections[index].order;
      sections[index].order = sections[swapIndex].order;
      sections[swapIndex].order = tempOrder;
      setHomepageSettings(prev => ({ ...prev, sections }));
    }
  };

  const addBadge = () => {
    setHomepageSettings(prev => ({
      ...prev,
      badges: [...prev.badges, { url: "", image_url: "", alt: "" }]
    }));
  };

  const updateBadge = (index, field, value) => {
    setHomepageSettings(prev => ({
      ...prev,
      badges: prev.badges.map((b, i) => i === index ? { ...b, [field]: value } : b)
    }));
  };

  const removeBadge = (index) => {
    setHomepageSettings(prev => ({
      ...prev,
      badges: prev.badges.filter((_, i) => i !== index)
    }));
  };

  // ========== LOGIN SCREEN ==========
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4" data-testid="admin-login-page">
        <div className="myspace-container max-w-md w-full">
          <div className="myspace-header">
            <h3>admin login</h3>
          </div>
          <div className="myspace-section m-2">
            <div className="myspace-section-title">Enter Credentials</div>
            <div className="myspace-section-content">
              <div className="mb-3">
                <label className="block text-[11px] text-[#cccccc] mb-1">Username:</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="input-2006 w-full"
                  data-testid="admin-username"
                />
              </div>
              <div className="mb-3">
                <label className="block text-[11px] text-[#cccccc] mb-1">Password:</label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="input-2006 w-full"
                  data-testid="admin-password"
                />
              </div>
              <button 
                type="button"
                onClick={handleLogin}
                className="btn-2006 btn-2006-orange w-full flex items-center justify-center gap-2"
                data-testid="admin-login-btn"
              >
                <LogIn size={14} />
                Login
              </button>
            </div>
          </div>
          <div className="text-center text-[10px] text-[#666] py-3">
            <a href="/" className="text-[#66ccff]">&laquo; Back to Home</a>
          </div>
        </div>
      </div>
    );
  }

  // ========== ADMIN PANEL ==========
  return (
    <div className="min-h-screen bg-black p-4" data-testid="admin-page">
      <div className="myspace-container">
        <div className="myspace-header flex justify-between items-center">
          <h3>admin panel</h3>
          <button onClick={handleLogout} className="btn-2006 text-[10px] flex items-center gap-1">
            <LogOut size={12} /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="tabs-2006 mx-2 mt-2 flex-wrap">
          {["albums", "videos", "blogs", "news", "photos", "comments", "socials", "uploads", "analytics", "homepage", "about", "secret", "settings"].map(tab => (
            <button 
              key={tab}
              type="button"
              className={`tab-2006 ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="myspace-section m-2" style={{ borderTop: 'none' }}>
          <div className="myspace-section-content">
            
            {/* ALBUMS */}
            {activeTab === 'albums' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#ff6600] font-bold">Manage Albums</span>
                  <button type="button" className="btn-2006" onClick={() => { setEditingAlbum(null); setAlbumForm({ title: "", cover_url: "", bandcamp_url: "", release_date: "", order: 0 }); setShowAlbumModal(true); }}>
                    <Plus size={12} /> Add
                  </button>
                </div>
                <table className="table-2006">
                  <thead><tr><th>Cover</th><th>Title</th><th>Actions</th></tr></thead>
                  <tbody>
                    {albums.map((album) => (
                      <tr key={album.id}>
                        <td><img src={album.cover_url} alt="" className="w-10 h-10 object-cover" /></td>
                        <td>{album.title}</td>
                        <td>
                          <button type="button" className="btn-2006 mr-1" onClick={() => { setEditingAlbum(album); setAlbumForm(album); setShowAlbumModal(true); }}><Edit size={12} /></button>
                          <button type="button" className="btn-2006 btn-2006-orange" onClick={() => confirmDelete('album', album.id, album.title)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* VIDEOS */}
            {activeTab === 'videos' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#ff6600] font-bold">Manage Videos</span>
                  <button type="button" className="btn-2006" onClick={() => { setEditingVideo(null); setVideoForm({ title: "", youtube_id: "", category: "recommended", is_featured: false }); setShowVideoModal(true); }}>
                    <Plus size={12} /> Add
                  </button>
                </div>
                <table className="table-2006">
                  <thead><tr><th>Title</th><th>YouTube ID</th><th>Category</th><th>Featured</th><th>Actions</th></tr></thead>
                  <tbody>
                    {videos.map((video) => (
                      <tr key={video.id}>
                        <td>{video.title}</td>
                        <td>{video.youtube_id}</td>
                        <td>{video.category}</td>
                        <td>{video.is_featured ? "Yes" : "No"}</td>
                        <td>
                          <button type="button" className="btn-2006 mr-1" onClick={() => { setEditingVideo(video); setVideoForm(video); setShowVideoModal(true); }}><Edit size={12} /></button>
                          <button type="button" className="btn-2006 btn-2006-orange" onClick={() => confirmDelete('video', video.id, video.title)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* BLOGS */}
            {activeTab === 'blogs' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#ff6600] font-bold">Manage Blogs</span>
                  <button type="button" className="btn-2006" onClick={() => { setEditingBlog(null); setBlogForm({ title: "", content: "", excerpt: "" }); setShowBlogModal(true); }}>
                    <Plus size={12} /> Add
                  </button>
                </div>
                <table className="table-2006">
                  <thead><tr><th>Title</th><th>Excerpt</th><th>Actions</th></tr></thead>
                  <tbody>
                    {blogs.map((blog) => (
                      <tr key={blog.id}>
                        <td>{blog.title}</td>
                        <td className="max-w-xs truncate">{blog.excerpt}</td>
                        <td>
                          <button type="button" className="btn-2006 mr-1" onClick={() => { setEditingBlog(blog); setBlogForm(blog); setShowBlogModal(true); }}><Edit size={12} /></button>
                          <button type="button" className="btn-2006 btn-2006-orange" onClick={() => confirmDelete('blog', blog.id, blog.title)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* NEWS */}
            {activeTab === 'news' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#ff6600] font-bold">Manage News</span>
                  <button type="button" className="btn-2006" onClick={() => { setEditingNews(null); setNewsForm({ content: "" }); setShowNewsModal(true); }}>
                    <Plus size={12} /> Add
                  </button>
                </div>
                <table className="table-2006">
                  <thead><tr><th>Content</th><th>Actions</th></tr></thead>
                  <tbody>
                    {news.map((item) => (
                      <tr key={item.id}>
                        <td className="max-w-md">{item.content}</td>
                        <td>
                          <button type="button" className="btn-2006 mr-1" onClick={() => { setEditingNews(item); setNewsForm(item); setShowNewsModal(true); }}><Edit size={12} /></button>
                          <button type="button" className="btn-2006 btn-2006-orange" onClick={() => confirmDelete('news', item.id, 'this news item')}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* PHOTOS */}
            {activeTab === 'photos' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#ff6600] font-bold">Manage Photos</span>
                  <button type="button" className="btn-2006" onClick={() => { setEditingPhoto(null); setPhotoForm({ title: "", description: "", post_type: "single", images: [""] }); setShowPhotoModal(true); }}>
                    <Plus size={12} /> Add
                  </button>
                </div>
                <table className="table-2006">
                  <thead><tr><th>Title</th><th>Type</th><th>Images</th><th>Actions</th></tr></thead>
                  <tbody>
                    {photos.map((photo) => (
                      <tr key={photo.id}>
                        <td>{photo.title}</td>
                        <td>{photo.post_type}</td>
                        <td>{photo.images?.length || 0}</td>
                        <td>
                          <button type="button" className="btn-2006 mr-1" onClick={() => { setEditingPhoto(photo); setPhotoForm({ ...photo, images: photo.images?.length > 0 ? photo.images : [""] }); setShowPhotoModal(true); }}><Edit size={12} /></button>
                          <button type="button" className="btn-2006 btn-2006-orange" onClick={() => confirmDelete('photo', photo.id, photo.title)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* COMMENTS */}
            {activeTab === 'comments' && (
              <div>
                <div className="mb-3">
                  <span className="text-[#ff6600] font-bold">Manage Comments ({comments.length})</span>
                </div>
                <table className="table-2006">
                  <thead><tr><th>Name</th><th>Message</th><th>Actions</th></tr></thead>
                  <tbody>
                    {comments.map((comment) => (
                      <tr key={comment.id}>
                        <td className="text-[#00ddff]">{comment.name}</td>
                        <td>{comment.message}</td>
                        <td>
                          <button type="button" className="btn-2006 btn-2006-orange" onClick={() => confirmDelete('comment', comment.id, 'this comment')}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* SOCIALS */}
            {activeTab === 'socials' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#ff6600] font-bold">Manage Social Links</span>
                  <button type="button" className="btn-2006" onClick={() => { setEditingSocial(null); setSocialForm({ platform: "youtube", url: "", display_name: "", order: 0, is_active: true }); setShowSocialModal(true); }}>
                    <Plus size={12} /> Add
                  </button>
                </div>
                <table className="table-2006">
                  <thead><tr><th>Platform</th><th>Display Name</th><th>URL</th><th>Active</th><th>Actions</th></tr></thead>
                  <tbody>
                    {socials.map((social) => (
                      <tr key={social.id}>
                        <td className="capitalize">{social.platform}</td>
                        <td>{social.display_name}</td>
                        <td className="max-w-[200px] truncate"><a href={social.url} target="_blank" rel="noopener noreferrer">{social.url}</a></td>
                        <td>{social.is_active ? "Yes" : "No"}</td>
                        <td>
                          <button type="button" className="btn-2006 mr-1" onClick={() => { setEditingSocial(social); setSocialForm(social); setShowSocialModal(true); }}><Edit size={12} /></button>
                          <button type="button" className="btn-2006 btn-2006-orange" onClick={() => confirmDelete('social', social.id, social.display_name)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* UPLOADS */}
            {activeTab === 'uploads' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[#ff6600] font-bold">Image Uploads</span>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      className="btn-2006"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload size={12} /> {uploading ? "Uploading..." : "Upload Image"}
                    </button>
                  </div>
                </div>
                <p className="text-[10px] text-[#888] mb-3">Max 5MB. Supported: JPG, PNG, GIF, WebP. Click a URL to copy it.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {uploads.map((file) => (
                    <div key={file.filename} className="border border-[#1a4a7a] bg-[#050d1a] p-2">
                      <img src={`${API.replace('/api', '')}${file.url}`} alt={file.filename} className="w-full h-24 object-cover mb-2" />
                      <div className="text-[10px] text-[#ccc] truncate mb-1">{file.filename}</div>
                      <div className="text-[9px] text-[#888] mb-2">{(file.size / 1024).toFixed(1)} KB</div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          className="btn-2006 flex-1 text-[9px]"
                          onClick={() => {
                            navigator.clipboard.writeText(`${API.replace('/api', '')}${file.url}`);
                            toast.success("URL copied!");
                          }}
                        >
                          Copy URL
                        </button>
                        <button type="button" className="btn-2006 btn-2006-orange" onClick={() => confirmDelete('upload', file.filename, file.filename)}><Trash2 size={10} /></button>
                      </div>
                    </div>
                  ))}
                </div>
                {uploads.length === 0 && <p className="text-[#888] text-center py-4">No images uploaded yet.</p>}
              </div>
            )}

            {/* ANALYTICS */}
            {activeTab === 'analytics' && (
              <div>
                <div className="mb-4">
                  <span className="text-[#ff6600] font-bold">Visitor Analytics</span>
                </div>
                
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="border border-[#1a4a7a] bg-[#050d1a] p-3 text-center">
                    <Eye size={20} className="mx-auto mb-2 text-[#00ddff]" />
                    <div className="text-2xl font-bold text-[#ff6600]">{analytics.total_views}</div>
                    <div className="text-[10px] text-[#888]">Total Views</div>
                  </div>
                  <div className="border border-[#1a4a7a] bg-[#050d1a] p-3 text-center">
                    <Users size={20} className="mx-auto mb-2 text-[#00ddff]" />
                    <div className="text-2xl font-bold text-[#ff6600]">{analytics.unique_visitors}</div>
                    <div className="text-[10px] text-[#888]">Unique Visitors</div>
                  </div>
                  <div className="border border-[#1a4a7a] bg-[#050d1a] p-3 text-center">
                    <TrendingUp size={20} className="mx-auto mb-2 text-[#00ddff]" />
                    <div className="text-2xl font-bold text-[#ff6600]">{analytics.views_today}</div>
                    <div className="text-[10px] text-[#888]">Views Today</div>
                  </div>
                  <div className="border border-[#1a4a7a] bg-[#050d1a] p-3 text-center">
                    <BarChart3 size={20} className="mx-auto mb-2 text-[#00ddff]" />
                    <div className="text-2xl font-bold text-[#ff6600]">{analytics.views_this_week}</div>
                    <div className="text-[10px] text-[#888]">Views This Week</div>
                  </div>
                </div>

                {/* Popular Pages */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-[#1a4a7a] bg-[#050d1a] p-3">
                    <div className="text-[#ff6600] font-bold mb-2 text-[12px]">Popular Pages</div>
                    {analytics.popular_pages.length > 0 ? (
                      <table className="w-full text-[11px]">
                        <tbody>
                          {analytics.popular_pages.map((page, i) => (
                            <tr key={i} className="border-b border-[#1a4a7a]">
                              <td className="py-1 text-[#66ccff]">{page.page}</td>
                              <td className="py-1 text-right text-[#ccc]">{page.views} views</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-[#888] text-[10px]">No data yet</p>
                    )}
                  </div>

                  <div className="border border-[#1a4a7a] bg-[#050d1a] p-3">
                    <div className="text-[#ff6600] font-bold mb-2 text-[12px]">Recent Activity</div>
                    {analytics.recent_views.length > 0 ? (
                      <div className="max-h-[200px] overflow-y-auto">
                        {analytics.recent_views.slice(0, 10).map((view, i) => (
                          <div key={i} className="text-[10px] py-1 border-b border-[#1a4a7a]">
                            <span className="text-[#66ccff]">{view.page}</span>
                            <span className="text-[#888] ml-2">{new Date(view.timestamp).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#888] text-[10px]">No recent activity</p>
                    )}
                  </div>
                </div>

                {/* Daily Chart */}
                {dailyStats.length > 0 && (
                  <div className="border border-[#1a4a7a] bg-[#050d1a] p-3 mt-4">
                    <div className="text-[#ff6600] font-bold mb-2 text-[12px]">Last 30 Days</div>
                    <div className="flex items-end gap-1 h-24">
                      {dailyStats.map((day, i) => {
                        const maxViews = Math.max(...dailyStats.map(d => d.views), 1);
                        const height = (day.views / maxViews) * 100;
                        return (
                          <div
                            key={i}
                            className="flex-1 bg-[#ff6600] hover:bg-[#00ddff] transition-colors"
                            style={{ height: `${Math.max(height, 2)}%` }}
                            title={`${day.date}: ${day.views} views`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[9px] text-[#888] mt-1">
                      <span>{dailyStats[0]?.date}</span>
                      <span>{dailyStats[dailyStats.length - 1]?.date}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* HOMEPAGE SECTIONS */}
            {activeTab === 'homepage' && (
              <div>
                <div className="mb-4">
                  <span className="text-[#ff6600] font-bold">Homepage Section Editor</span>
                  <p className="text-[10px] text-[#888] mt-1">Toggle visibility, reorder sections, customize titles, and set per-section colors. Empty color fields will use global theme colors.</p>
                </div>

                {/* Section Controls */}
                <div className="space-y-4 mb-6">
                  {[...homepageSettings.sections].sort((a, b) => a.order - b.order).map((section, idx) => (
                    <div key={section.key} className="border border-[#1a4a7a] bg-[#050d1a] p-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={section.visible}
                            onChange={(e) => updateSection(section.key, 'visible', e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span className="text-[#00ddff] font-bold text-[12px] capitalize">{section.key.replace('_', ' ')}</span>
                        </div>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className="btn-2006 text-[10px] px-2"
                            onClick={() => moveSection(section.key, 'up')}
                            disabled={idx === 0}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="btn-2006 text-[10px] px-2"
                            onClick={() => moveSection(section.key, 'down')}
                            disabled={idx === homepageSettings.sections.length - 1}
                          >
                            ▼
                          </button>
                        </div>
                      </div>
                      
                      {section.key !== 'badges' && (
                        <div className="mb-3">
                          <label className="block text-[10px] text-[#888] mb-1">Section Title:</label>
                          <input
                            type="text"
                            value={section.title}
                            onChange={(e) => updateSection(section.key, 'title', e.target.value)}
                            className="input-2006 w-full"
                            placeholder="Section title..."
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <label className="block text-[9px] text-[#888] mb-1">Background:</label>
                          <div className="flex gap-1">
                            <input 
                              type="color" 
                              value={section.colors.bg || "#0d1f3c"} 
                              onChange={(e) => updateSectionColor(section.key, 'bg', e.target.value)} 
                              className="w-8 h-6"
                            />
                            <input 
                              className="input-2006 flex-1 text-[10px]" 
                              value={section.colors.bg || ""} 
                              onChange={(e) => updateSectionColor(section.key, 'bg', e.target.value)}
                              placeholder="global"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[9px] text-[#888] mb-1">Text:</label>
                          <div className="flex gap-1">
                            <input 
                              type="color" 
                              value={section.colors.text || "#ffffff"} 
                              onChange={(e) => updateSectionColor(section.key, 'text', e.target.value)} 
                              className="w-8 h-6"
                            />
                            <input 
                              className="input-2006 flex-1 text-[10px]" 
                              value={section.colors.text || ""} 
                              onChange={(e) => updateSectionColor(section.key, 'text', e.target.value)}
                              placeholder="global"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[9px] text-[#888] mb-1">Border:</label>
                          <div className="flex gap-1">
                            <input 
                              type="color" 
                              value={section.colors.border || "#1a4a7a"} 
                              onChange={(e) => updateSectionColor(section.key, 'border', e.target.value)} 
                              className="w-8 h-6"
                            />
                            <input 
                              className="input-2006 flex-1 text-[10px]" 
                              value={section.colors.border || ""} 
                              onChange={(e) => updateSectionColor(section.key, 'border', e.target.value)}
                              placeholder="global"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[9px] text-[#888] mb-1">Accent:</label>
                          <div className="flex gap-1">
                            <input 
                              type="color" 
                              value={section.colors.accent || "#ff6600"} 
                              onChange={(e) => updateSectionColor(section.key, 'accent', e.target.value)} 
                              className="w-8 h-6"
                            />
                            <input 
                              className="input-2006 flex-1 text-[10px]" 
                              value={section.colors.accent || ""} 
                              onChange={(e) => updateSectionColor(section.key, 'accent', e.target.value)}
                              placeholder="global"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Badges Section */}
                <div className="border-t border-[#1a4a7a] pt-4 mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <span className="text-[#ff6600] font-bold">Web Badges (88x31)</span>
                      <p className="text-[10px] text-[#888] mt-1">Classic web badges displayed at the bottom left of the homepage.</p>
                    </div>
                    <button type="button" className="btn-2006" onClick={addBadge}>
                      <Plus size={12} /> Add Badge
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {homepageSettings.badges.map((badge, idx) => (
                      <div key={idx} className="flex gap-3 items-center border border-[#1a4a7a] bg-[#050d1a] p-2">
                        {badge.image_url && (
                          <img src={badge.image_url} alt={badge.alt} className="w-[88px] h-[31px] object-contain border border-[#333]" />
                        )}
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            value={badge.image_url}
                            onChange={(e) => updateBadge(idx, 'image_url', e.target.value)}
                            className="input-2006 text-[10px]"
                            placeholder="Image URL (88x31)"
                          />
                          <input
                            type="text"
                            value={badge.url}
                            onChange={(e) => updateBadge(idx, 'url', e.target.value)}
                            className="input-2006 text-[10px]"
                            placeholder="Link URL"
                          />
                          <input
                            type="text"
                            value={badge.alt}
                            onChange={(e) => updateBadge(idx, 'alt', e.target.value)}
                            className="input-2006 text-[10px]"
                            placeholder="Alt text"
                          />
                        </div>
                        <button type="button" className="btn-2006 btn-2006-orange" onClick={() => removeBadge(idx)}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    {homepageSettings.badges.length === 0 && (
                      <p className="text-[10px] text-[#888] text-center py-2">No badges yet. Add one above!</p>
                    )}
                  </div>
                </div>

                <button type="button" className="btn-2006 btn-2006-orange" onClick={handleHomepageSubmit}>
                  Save Homepage Settings
                </button>
              </div>
            )}

            {/* ABOUT */}
            {activeTab === 'about' && (
              <div>
                <div className="mb-3">
                  <span className="text-[#ff6600] font-bold">Edit About Page</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] text-[#ccc] mb-1">Title:</label>
                    <input className="input-2006 w-full" value={about.title || ""} onChange={(e) => setAbout({...about, title: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[#ccc] mb-1">Photo URL:</label>
                    <input className="input-2006 w-full" value={about.photo_url || ""} onChange={(e) => setAbout({...about, photo_url: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[#ccc] mb-1">Content:</label>
                    <textarea className="textarea-2006 w-full h-40" value={about.content || ""} onChange={(e) => setAbout({...about, content: e.target.value})} />
                  </div>
                  <button type="button" className="btn-2006 btn-2006-orange" onClick={handleAboutSubmit}>Save About Page</button>
                </div>
              </div>
            )}

            {/* SECRET PAGE */}
            {activeTab === 'secret' && (
              <div>
                <div className="mb-3">
                  <span className="text-[#ff6600] font-bold">Secret Page (Holly's Page)</span>
                  <p className="text-[10px] text-[#888] mt-1">This is the hidden page accessible via the secret holly icon on the homepage.</p>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-[11px] text-[#ccc] mb-1">Content (poems, letters, etc.):</label>
                    <textarea 
                      className="textarea-2006 w-full h-60" 
                      value={secretPage.content || ""} 
                      onChange={(e) => setSecretPage({...secretPage, content: e.target.value})}
                      placeholder="Write your secret message here..."
                      style={{ fontFamily: 'Georgia, serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[#ccc] mb-1">Song URL (will loop on this page):</label>
                    <input 
                      className="input-2006 w-full" 
                      value={secretPage.song_url || ""} 
                      onChange={(e) => setSecretPage({...secretPage, song_url: e.target.value})}
                      placeholder="/admiration.mp3 or https://..."
                    />
                    <p className="text-[9px] text-[#666] mt-1">Default: /admiration.mp3 (uploaded song)</p>
                  </div>
                  <div>
                    <label className="block text-[11px] text-[#ccc] mb-1">Background Image URL (optional):</label>
                    <input 
                      className="input-2006 w-full" 
                      value={secretPage.bg_image || ""} 
                      onChange={(e) => setSecretPage({...secretPage, bg_image: e.target.value})}
                      placeholder="Leave empty for black background, or enter image URL..."
                    />
                    <p className="text-[9px] text-[#666] mt-1">Leave empty for default black background. Enter a URL for a custom background image.</p>
                  </div>
                  <button type="button" className="btn-2006 btn-2006-orange" onClick={handleSecretSubmit}>Save Secret Page</button>
                </div>
              </div>
            )}

            {/* SETTINGS */}
            {activeTab === 'settings' && (
              <div>
                {/* PRESETS SECTION */}
                <div className="mb-6">
                  <div className="mb-3">
                    <span className="text-[#ff6600] font-bold">Quick Presets</span>
                    <p className="text-[10px] text-[#888] mt-1">Click a preset to apply it instantly. Don't forget to save!</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      {
                        name: "Original Blue",
                        desc: "Classic dark blue & orange",
                        colors: { bg_body: "#000000", bg_container: "#0a1628", bg_section: "#0d1f3c", accent_orange: "#ff6600", accent_cyan: "#00ddff", text_white: "#ffffff", text_link: "#66ccff", border_color: "#1a4a7a" },
                        fonts: { font_heading: "Verdana", font_body: "Verdana", font_nav: "Verdana" },
                        preview: ["#000000", "#0a1628", "#ff6600", "#00ddff"]
                      },
                      {
                        name: "Deep Space",
                        desc: "Dark purple cosmos",
                        colors: { bg_body: "#0d0015", bg_container: "#1a0a2e", bg_section: "#2a1548", accent_orange: "#ff44cc", accent_cyan: "#44ffff", text_white: "#ffffff", text_link: "#bb88ff", border_color: "#4a2a7a" },
                        fonts: { font_heading: "Georgia", font_body: "Verdana", font_nav: "Verdana" },
                        preview: ["#0d0015", "#1a0a2e", "#ff44cc", "#44ffff"]
                      },
                      {
                        name: "Blood Moon",
                        desc: "Dark red atmosphere",
                        colors: { bg_body: "#120000", bg_container: "#1f0808", bg_section: "#2d1212", accent_orange: "#ff2222", accent_cyan: "#ff8888", text_white: "#ffffff", text_link: "#ff6666", border_color: "#551111" },
                        fonts: { font_heading: "Impact", font_body: "Tahoma", font_nav: "Tahoma" },
                        preview: ["#120000", "#1f0808", "#ff2222", "#ff8888"]
                      },
                      {
                        name: "Emerald Forest",
                        desc: "Lush green jungle",
                        colors: { bg_body: "#001208", bg_container: "#0a2015", bg_section: "#123020", accent_orange: "#22ff66", accent_cyan: "#88ffaa", text_white: "#ffffff", text_link: "#66ff99", border_color: "#115533" },
                        fonts: { font_heading: "Trebuchet MS", font_body: "Verdana", font_nav: "Verdana" },
                        preview: ["#001208", "#0a2015", "#22ff66", "#88ffaa"]
                      },
                      {
                        name: "Arctic Ice",
                        desc: "Cool frozen tundra",
                        colors: { bg_body: "#001520", bg_container: "#082838", bg_section: "#103848", accent_orange: "#00ccff", accent_cyan: "#88eeff", text_white: "#ffffff", text_link: "#66ddff", border_color: "#1166aa" },
                        fonts: { font_heading: "Arial", font_body: "Arial", font_nav: "Arial" },
                        preview: ["#001520", "#082838", "#00ccff", "#88eeff"]
                      },
                      {
                        name: "Volcanic",
                        desc: "Molten lava heat",
                        colors: { bg_body: "#150800", bg_container: "#281408", bg_section: "#3a2010", accent_orange: "#ff6600", accent_cyan: "#ffaa44", text_white: "#ffffff", text_link: "#ff8833", border_color: "#884400" },
                        fonts: { font_heading: "Impact", font_body: "Tahoma", font_nav: "Tahoma" },
                        preview: ["#150800", "#281408", "#ff6600", "#ffaa44"]
                      },
                      {
                        name: "Candy Pink",
                        desc: "Sweet bubblegum vibes",
                        colors: { bg_body: "#150815", bg_container: "#281428", bg_section: "#3a1e3a", accent_orange: "#ff66aa", accent_cyan: "#ffaadd", text_white: "#ffffff", text_link: "#ff88cc", border_color: "#883366" },
                        fonts: { font_heading: "Comic Sans MS", font_body: "Comic Sans MS", font_nav: "Comic Sans MS" },
                        preview: ["#150815", "#281428", "#ff66aa", "#ffaadd"]
                      },
                      {
                        name: "Neon Night",
                        desc: "Cyberpunk city lights",
                        colors: { bg_body: "#08000f", bg_container: "#120020", bg_section: "#1a0030", accent_orange: "#ff0088", accent_cyan: "#00ffff", text_white: "#ffffff", text_link: "#ff66bb", border_color: "#440066" },
                        fonts: { font_heading: "Impact", font_body: "Arial", font_nav: "Arial" },
                        preview: ["#08000f", "#120020", "#ff0088", "#00ffff"]
                      },
                      {
                        name: "Royal Navy",
                        desc: "Classic naval theme",
                        colors: { bg_body: "#000820", bg_container: "#001540", bg_section: "#002060", accent_orange: "#ffd700", accent_cyan: "#87ceeb", text_white: "#ffffff", text_link: "#add8e6", border_color: "#003080" },
                        fonts: { font_heading: "Times New Roman", font_body: "Georgia", font_nav: "Georgia" },
                        preview: ["#000820", "#001540", "#ffd700", "#87ceeb"]
                      },
                      {
                        name: "Matrix",
                        desc: "Digital rain",
                        colors: { bg_body: "#000800", bg_container: "#001500", bg_section: "#002200", accent_orange: "#00ff00", accent_cyan: "#44ff44", text_white: "#00ff00", text_link: "#22cc22", border_color: "#004400" },
                        fonts: { font_heading: "Courier New", font_body: "Courier New", font_nav: "Courier New" },
                        preview: ["#000800", "#001500", "#00ff00", "#44ff44"]
                      },
                      {
                        name: "Sunset Beach",
                        desc: "Warm tropical evening",
                        colors: { bg_body: "#1a0a05", bg_container: "#2d1508", bg_section: "#402010", accent_orange: "#ff8844", accent_cyan: "#ffbb88", text_white: "#ffffff", text_link: "#ffaa66", border_color: "#884422" },
                        fonts: { font_heading: "Georgia", font_body: "Tahoma", font_nav: "Tahoma" },
                        preview: ["#1a0a05", "#2d1508", "#ff8844", "#ffbb88"]
                      },
                      {
                        name: "Midnight Blue",
                        desc: "Deep ocean depths",
                        colors: { bg_body: "#000510", bg_container: "#000a20", bg_section: "#001030", accent_orange: "#4488ff", accent_cyan: "#88bbff", text_white: "#ffffff", text_link: "#6699ff", border_color: "#224488" },
                        fonts: { font_heading: "Arial", font_body: "Verdana", font_nav: "Verdana" },
                        preview: ["#000510", "#000a20", "#4488ff", "#88bbff"]
                      },
                      {
                        name: "Amethyst",
                        desc: "Royal purple gem",
                        colors: { bg_body: "#0a0515", bg_container: "#150a28", bg_section: "#201040", accent_orange: "#aa66ff", accent_cyan: "#cc99ff", text_white: "#ffffff", text_link: "#bb88ff", border_color: "#553388" },
                        fonts: { font_heading: "Georgia", font_body: "Georgia", font_nav: "Georgia" },
                        preview: ["#0a0515", "#150a28", "#aa66ff", "#cc99ff"]
                      },
                      {
                        name: "Bronze Age",
                        desc: "Ancient warrior style",
                        colors: { bg_body: "#100a05", bg_container: "#1f1508", bg_section: "#2e2010", accent_orange: "#cd7f32", accent_cyan: "#daa06d", text_white: "#f5deb3", text_link: "#d4a574", border_color: "#8b4513" },
                        fonts: { font_heading: "Times New Roman", font_body: "Georgia", font_nav: "Georgia" },
                        preview: ["#100a05", "#1f1508", "#cd7f32", "#daa06d"]
                      },
                      {
                        name: "Toxic Waste",
                        desc: "Radioactive glow",
                        colors: { bg_body: "#050a00", bg_container: "#0a1500", bg_section: "#102000", accent_orange: "#aaff00", accent_cyan: "#ccff66", text_white: "#ffffff", text_link: "#bbff33", border_color: "#446600" },
                        fonts: { font_heading: "Impact", font_body: "Arial", font_nav: "Arial" },
                        preview: ["#050a00", "#0a1500", "#aaff00", "#ccff66"]
                      },
                      {
                        name: "Cherry Blossom",
                        desc: "Soft pink spring",
                        colors: { bg_body: "#120810", bg_container: "#201018", bg_section: "#2e1820", accent_orange: "#ffb7c5", accent_cyan: "#ffd1dc", text_white: "#ffffff", text_link: "#ffc0cb", border_color: "#8b4560" },
                        fonts: { font_heading: "Georgia", font_body: "Tahoma", font_nav: "Tahoma" },
                        preview: ["#120810", "#201018", "#ffb7c5", "#ffd1dc"]
                      },
                      // ========== LIGHT THEMES ==========
                      {
                        name: "Clean White",
                        desc: "Minimalist bright",
                        colors: { bg_body: "#ffffff", bg_container: "#f0f0f0", bg_section: "#e8e8e8", accent_orange: "#0066cc", accent_cyan: "#0099ff", text_white: "#222222", text_link: "#0066cc", border_color: "#cccccc" },
                        fonts: { font_heading: "Arial", font_body: "Arial", font_nav: "Arial" },
                        preview: ["#ffffff", "#f0f0f0", "#0066cc", "#0099ff"]
                      },
                      {
                        name: "Light Gray",
                        desc: "Soft neutral tones",
                        colors: { bg_body: "#e0e0e0", bg_container: "#d0d0d0", bg_section: "#c8c8c8", accent_orange: "#ff6600", accent_cyan: "#0088cc", text_white: "#1a1a1a", text_link: "#0066aa", border_color: "#999999" },
                        fonts: { font_heading: "Verdana", font_body: "Verdana", font_nav: "Verdana" },
                        preview: ["#e0e0e0", "#d0d0d0", "#ff6600", "#0088cc"]
                      },
                      {
                        name: "Pastel Pink",
                        desc: "Soft kawaii vibes",
                        colors: { bg_body: "#ffe4ec", bg_container: "#ffd1dc", bg_section: "#ffb6c1", accent_orange: "#ff69b4", accent_cyan: "#ff1493", text_white: "#4a2040", text_link: "#cc3399", border_color: "#ff99aa" },
                        fonts: { font_heading: "Comic Sans MS", font_body: "Comic Sans MS", font_nav: "Comic Sans MS" },
                        preview: ["#ffe4ec", "#ffd1dc", "#ff69b4", "#ff1493"]
                      },
                      {
                        name: "Pastel Blue",
                        desc: "Calm sky colors",
                        colors: { bg_body: "#e6f3ff", bg_container: "#cce5ff", bg_section: "#b3d9ff", accent_orange: "#3399ff", accent_cyan: "#0066cc", text_white: "#1a3a5c", text_link: "#0055aa", border_color: "#88bbdd" },
                        fonts: { font_heading: "Arial", font_body: "Tahoma", font_nav: "Tahoma" },
                        preview: ["#e6f3ff", "#cce5ff", "#3399ff", "#0066cc"]
                      },
                      {
                        name: "Pastel Green",
                        desc: "Fresh mint vibes",
                        colors: { bg_body: "#e8f5e9", bg_container: "#c8e6c9", bg_section: "#a5d6a7", accent_orange: "#4caf50", accent_cyan: "#2e7d32", text_white: "#1b3d1c", text_link: "#388e3c", border_color: "#81c784" },
                        fonts: { font_heading: "Trebuchet MS", font_body: "Verdana", font_nav: "Verdana" },
                        preview: ["#e8f5e9", "#c8e6c9", "#4caf50", "#2e7d32"]
                      },
                      {
                        name: "Pastel Lavender",
                        desc: "Dreamy purple haze",
                        colors: { bg_body: "#f3e5f5", bg_container: "#e1bee7", bg_section: "#ce93d8", accent_orange: "#9c27b0", accent_cyan: "#7b1fa2", text_white: "#3a1c48", text_link: "#8e24aa", border_color: "#ba68c8" },
                        fonts: { font_heading: "Georgia", font_body: "Georgia", font_nav: "Georgia" },
                        preview: ["#f3e5f5", "#e1bee7", "#9c27b0", "#7b1fa2"]
                      },
                      {
                        name: "Pastel Yellow",
                        desc: "Sunny lemonade",
                        colors: { bg_body: "#fffde7", bg_container: "#fff9c4", bg_section: "#fff59d", accent_orange: "#ffc107", accent_cyan: "#ff9800", text_white: "#4a4000", text_link: "#f57c00", border_color: "#ffcc80" },
                        fonts: { font_heading: "Comic Sans MS", font_body: "Arial", font_nav: "Arial" },
                        preview: ["#fffde7", "#fff9c4", "#ffc107", "#ff9800"]
                      },
                      {
                        name: "Cream Paper",
                        desc: "Vintage document",
                        colors: { bg_body: "#faf8f0", bg_container: "#f5f0e0", bg_section: "#ebe5d0", accent_orange: "#8b4513", accent_cyan: "#a0522d", text_white: "#3c2415", text_link: "#6b3a0a", border_color: "#c4a882" },
                        fonts: { font_heading: "Times New Roman", font_body: "Georgia", font_nav: "Georgia" },
                        preview: ["#faf8f0", "#f5f0e0", "#8b4513", "#a0522d"]
                      },
                      {
                        name: "Peach Fuzz",
                        desc: "Warm and cozy",
                        colors: { bg_body: "#fff0e6", bg_container: "#ffe0cc", bg_section: "#ffd0b3", accent_orange: "#ff7043", accent_cyan: "#ff5722", text_white: "#4a2c20", text_link: "#e64a19", border_color: "#ffab91" },
                        fonts: { font_heading: "Georgia", font_body: "Tahoma", font_nav: "Tahoma" },
                        preview: ["#fff0e6", "#ffe0cc", "#ff7043", "#ff5722"]
                      },
                      {
                        name: "Baby Blue",
                        desc: "Soft infant blue",
                        colors: { bg_body: "#e3f2fd", bg_container: "#bbdefb", bg_section: "#90caf9", accent_orange: "#1976d2", accent_cyan: "#0d47a1", text_white: "#0d2744", text_link: "#1565c0", border_color: "#64b5f6" },
                        fonts: { font_heading: "Arial", font_body: "Arial", font_nav: "Arial" },
                        preview: ["#e3f2fd", "#bbdefb", "#1976d2", "#0d47a1"]
                      },
                      {
                        name: "Cotton Candy",
                        desc: "Sweet carnival treat",
                        colors: { bg_body: "#fce4ec", bg_container: "#f8bbd9", bg_section: "#f48fb1", accent_orange: "#e91e63", accent_cyan: "#ad1457", text_white: "#3c1028", text_link: "#c2185b", border_color: "#f06292" },
                        fonts: { font_heading: "Comic Sans MS", font_body: "Comic Sans MS", font_nav: "Comic Sans MS" },
                        preview: ["#fce4ec", "#f8bbd9", "#e91e63", "#ad1457"]
                      },
                      {
                        name: "Newspaper",
                        desc: "Classic print style",
                        colors: { bg_body: "#f5f5f5", bg_container: "#eeeeee", bg_section: "#e0e0e0", accent_orange: "#212121", accent_cyan: "#424242", text_white: "#000000", text_link: "#1a1a1a", border_color: "#9e9e9e" },
                        fonts: { font_heading: "Times New Roman", font_body: "Times New Roman", font_nav: "Times New Roman" },
                        preview: ["#f5f5f5", "#eeeeee", "#212121", "#424242"]
                      },
                      {
                        name: "Seafoam",
                        desc: "Ocean breeze",
                        colors: { bg_body: "#e0f7fa", bg_container: "#b2ebf2", bg_section: "#80deea", accent_orange: "#00acc1", accent_cyan: "#00838f", text_white: "#004d56", text_link: "#0097a7", border_color: "#4dd0e1" },
                        fonts: { font_heading: "Arial", font_body: "Verdana", font_nav: "Verdana" },
                        preview: ["#e0f7fa", "#b2ebf2", "#00acc1", "#00838f"]
                      },
                      {
                        name: "nezuai",
                        desc: "Dark urban indie",
                        colors: { bg_body: "#0a0a0a", bg_container: "#121212", bg_section: "#1a1a1a", accent_orange: "#c4a77d", accent_cyan: "#8a9a8a", text_white: "#d0d0d0", text_link: "#a0b0a0", border_color: "#2a2a2a" },
                        fonts: { font_heading: "Georgia", font_body: "Verdana", font_nav: "Verdana" },
                        preview: ["#0a0a0a", "#121212", "#c4a77d", "#8a9a8a"]
                      },
                      {
                        name: "Rose Gold",
                        desc: "Elegant metallic",
                        colors: { bg_body: "#fdf2f0", bg_container: "#f8e0dc", bg_section: "#f0cdc6", accent_orange: "#b76e79", accent_cyan: "#8e4a52", text_white: "#4a2528", text_link: "#a05060", border_color: "#d4a5a5" },
                        fonts: { font_heading: "Georgia", font_body: "Georgia", font_nav: "Georgia" },
                        preview: ["#fdf2f0", "#f8e0dc", "#b76e79", "#8e4a52"]
                      },
                    ].map((preset) => (
                      <button
                        key={preset.name}
                        type="button"
                        className="p-2 border border-[#1a4a7a] bg-[#050d1a] hover:border-[#ff6600] transition-colors text-left"
                        onClick={() => setSettings({ ...settings, ...preset.colors, ...preset.fonts })}
                      >
                        <div className="flex gap-1 mb-2">
                          {preset.preview.map((color, i) => (
                            <div key={i} className="w-4 h-4 border border-[#333]" style={{ backgroundColor: color }} />
                          ))}
                        </div>
                        <div className="text-[11px] text-[#ff6600] font-bold">{preset.name}</div>
                        <div className="text-[9px] text-[#888]">{preset.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#1a4a7a] pt-4 mb-3">
                  <span className="text-[#ff6600] font-bold">Custom Colors</span>
                  <p className="text-[10px] text-[#888] mt-1">Fine-tune individual colors</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'bg_body', label: 'Body BG' },
                    { key: 'bg_container', label: 'Container BG' },
                    { key: 'bg_section', label: 'Section BG' },
                    { key: 'accent_orange', label: 'Accent Orange' },
                    { key: 'accent_cyan', label: 'Accent Cyan' },
                    { key: 'text_white', label: 'Text Color' },
                    { key: 'text_link', label: 'Link Color' },
                    { key: 'border_color', label: 'Border Color' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-[10px] text-[#ccc] mb-1">{label}:</label>
                      <div className="flex gap-2">
                        <input type="color" value={settings[key] || "#000000"} onChange={(e) => setSettings({...settings, [key]: e.target.value})} className="w-10 h-8" />
                        <input className="input-2006 flex-1" value={settings[key] || ""} onChange={(e) => setSettings({...settings, [key]: e.target.value})} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t border-[#1a4a7a]">
                  <div className="mb-3">
                    <span className="text-[#ff6600] font-bold">Font Settings</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { key: 'font_heading', label: 'Headings' },
                      { key: 'font_body', label: 'Body Text' },
                      { key: 'font_nav', label: 'Navigation' },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-[10px] text-[#ccc] mb-1">{label}:</label>
                        <select className="input-2006 w-full" value={settings[key] || 'Verdana'} onChange={(e) => setSettings({...settings, [key]: e.target.value})}>
                          {['Verdana', 'Arial', 'Tahoma', 'Georgia', 'Times New Roman', 'Trebuchet MS', 'Comic Sans MS', 'Impact', 'Courier New'].map(font => (
                            <option key={font} value={font}>{font}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#1a4a7a] pt-4 mb-3 mt-4">
                  <span className="text-[#ff6600] font-bold">Background Image</span>
                  <p className="text-[10px] text-[#888] mt-1">Set a custom background image for the entire site (optional)</p>
                </div>
                <div>
                  <label className="block text-[10px] text-[#ccc] mb-1">Background Image URL:</label>
                  <input 
                    className="input-2006 w-full" 
                    value={settings.bg_image || ""} 
                    onChange={(e) => setSettings({...settings, bg_image: e.target.value})}
                    placeholder="Leave empty for solid color, or enter image URL..."
                  />
                  <p className="text-[9px] text-[#666] mt-1">Leave empty to use the solid background color. The image will cover the entire page.</p>
                </div>

                <button type="button" className="btn-2006 btn-2006-orange mt-4" onClick={handleSettingsSubmit}>Save Settings</button>
                <p className="text-[10px] text-[#888] mt-2">Refresh page after saving to see changes.</p>
              </div>
            )}
          </div>
        </div>

        {/* ========== MODALS ========== */}
        
        {/* Album Modal */}
        {showAlbumModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="myspace-container max-w-md w-full">
              <div className="myspace-header flex justify-between items-center">
                <h3>{editingAlbum ? 'Edit Album' : 'Add Album'}</h3>
                <button type="button" onClick={() => setShowAlbumModal(false)}><X size={16} /></button>
              </div>
              <div className="p-4 bg-[#050d1a]">
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Title:</label><input className="input-2006 w-full" value={albumForm.title} onChange={(e) => setAlbumForm({...albumForm, title: e.target.value})} /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Cover URL:</label><input className="input-2006 w-full" value={albumForm.cover_url} onChange={(e) => setAlbumForm({...albumForm, cover_url: e.target.value})} /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Bandcamp URL:</label><input className="input-2006 w-full" value={albumForm.bandcamp_url} onChange={(e) => setAlbumForm({...albumForm, bandcamp_url: e.target.value})} /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Release Date:</label><input className="input-2006 w-full" value={albumForm.release_date} onChange={(e) => setAlbumForm({...albumForm, release_date: e.target.value})} /></div>
                <div className="flex gap-2 mt-4">
                  <button type="button" className="btn-2006 btn-2006-orange flex-1" onClick={handleAlbumSubmit}>Save</button>
                  <button type="button" className="btn-2006 flex-1" onClick={() => setShowAlbumModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Video Modal */}
        {showVideoModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="myspace-container max-w-md w-full">
              <div className="myspace-header flex justify-between items-center">
                <h3>{editingVideo ? 'Edit Video' : 'Add Video'}</h3>
                <button type="button" onClick={() => setShowVideoModal(false)}><X size={16} /></button>
              </div>
              <div className="p-4 bg-[#050d1a]">
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Title:</label><input className="input-2006 w-full" value={videoForm.title} onChange={(e) => setVideoForm({...videoForm, title: e.target.value})} /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">YouTube ID:</label><input className="input-2006 w-full" value={videoForm.youtube_id} onChange={(e) => setVideoForm({...videoForm, youtube_id: e.target.value})} placeholder="e.g. dQw4w9WgXcQ" /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Category:</label><select className="input-2006 w-full" value={videoForm.category} onChange={(e) => setVideoForm({...videoForm, category: e.target.value})}><option value="latest">Latest</option><option value="popular">Popular</option><option value="recommended">Recommended</option></select></div>
                <div className="mb-2"><label className="flex items-center gap-2 text-[11px] text-[#ccc]"><input type="checkbox" checked={videoForm.is_featured} onChange={(e) => setVideoForm({...videoForm, is_featured: e.target.checked})} /> Featured on Homepage</label></div>
                <div className="flex gap-2 mt-4">
                  <button type="button" className="btn-2006 btn-2006-orange flex-1" onClick={handleVideoSubmit}>Save</button>
                  <button type="button" className="btn-2006 flex-1" onClick={() => setShowVideoModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog Modal */}
        {showBlogModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="myspace-container max-w-lg w-full">
              <div className="myspace-header flex justify-between items-center">
                <h3>{editingBlog ? 'Edit Blog' : 'Add Blog'}</h3>
                <button type="button" onClick={() => setShowBlogModal(false)}><X size={16} /></button>
              </div>
              <div className="p-4 bg-[#050d1a]">
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Title:</label><input className="input-2006 w-full" value={blogForm.title} onChange={(e) => setBlogForm({...blogForm, title: e.target.value})} /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Excerpt:</label><input className="input-2006 w-full" value={blogForm.excerpt} onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})} /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Content:</label><textarea className="textarea-2006 w-full h-40" value={blogForm.content} onChange={(e) => setBlogForm({...blogForm, content: e.target.value})} /></div>
                <div className="flex gap-2 mt-4">
                  <button type="button" className="btn-2006 btn-2006-orange flex-1" onClick={handleBlogSubmit}>Save</button>
                  <button type="button" className="btn-2006 flex-1" onClick={() => setShowBlogModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* News Modal */}
        {showNewsModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="myspace-container max-w-md w-full">
              <div className="myspace-header flex justify-between items-center">
                <h3>{editingNews ? 'Edit News' : 'Add News'}</h3>
                <button type="button" onClick={() => setShowNewsModal(false)}><X size={16} /></button>
              </div>
              <div className="p-4 bg-[#050d1a]">
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Content:</label><textarea className="textarea-2006 w-full h-32" value={newsForm.content} onChange={(e) => setNewsForm({...newsForm, content: e.target.value})} /></div>
                <div className="flex gap-2 mt-4">
                  <button type="button" className="btn-2006 btn-2006-orange flex-1" onClick={handleNewsSubmit}>Save</button>
                  <button type="button" className="btn-2006 flex-1" onClick={() => setShowNewsModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Photo Modal */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-auto">
            <div className="myspace-container max-w-lg w-full my-4">
              <div className="myspace-header flex justify-between items-center">
                <h3>{editingPhoto ? 'Edit Photo' : 'Add Photo'}</h3>
                <button type="button" onClick={() => setShowPhotoModal(false)}><X size={16} /></button>
              </div>
              <div className="p-4 bg-[#050d1a]">
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Title:</label><input className="input-2006 w-full" value={photoForm.title} onChange={(e) => setPhotoForm({...photoForm, title: e.target.value})} /></div>
                <div className="mb-2">
                  <label className="block text-[11px] text-[#ccc] mb-1">Type:</label>
                  <select className="input-2006 w-full" value={photoForm.post_type} onChange={(e) => setPhotoForm({...photoForm, post_type: e.target.value})}>
                    <option value="single">Single Image</option>
                    <option value="collage">Collage</option>
                    <option value="slideshow">Slideshow</option>
                  </select>
                </div>
                <div className="mb-2">
                  <label className="block text-[11px] text-[#ccc] mb-1">Image URLs:</label>
                  {photoForm.images.map((img, idx) => (
                    <div key={idx} className="flex gap-2 mb-1">
                      <input className="input-2006 flex-1" value={img} onChange={(e) => updateImageField(idx, e.target.value)} placeholder="https://..." />
                      {photoForm.images.length > 1 && <button type="button" className="btn-2006" onClick={() => removeImageField(idx)}><X size={12} /></button>}
                    </div>
                  ))}
                  <button type="button" className="btn-2006 mt-1" onClick={addImageField}><Plus size={12} /> Add Image</button>
                </div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Description:</label><textarea className="textarea-2006 w-full h-20" value={photoForm.description} onChange={(e) => setPhotoForm({...photoForm, description: e.target.value})} /></div>
                <div className="flex gap-2 mt-4">
                  <button type="button" className="btn-2006 btn-2006-orange flex-1" onClick={handlePhotoSubmit}>Save</button>
                  <button type="button" className="btn-2006 flex-1" onClick={() => setShowPhotoModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Social Link Modal */}
        {showSocialModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="myspace-container max-w-md w-full">
              <div className="myspace-header flex justify-between items-center">
                <h3>{editingSocial ? 'Edit Social Link' : 'Add Social Link'}</h3>
                <button type="button" onClick={() => setShowSocialModal(false)}><X size={16} /></button>
              </div>
              <div className="p-4 bg-[#050d1a]">
                <div className="mb-2">
                  <label className="block text-[11px] text-[#ccc] mb-1">Platform:</label>
                  <select className="input-2006 w-full" value={socialForm.platform} onChange={(e) => setSocialForm({...socialForm, platform: e.target.value})}>
                    <option value="youtube">YouTube</option>
                    <option value="twitch">Twitch</option>
                    <option value="instagram">Instagram</option>
                    <option value="twitter">Twitter/X</option>
                    <option value="spotify">Spotify</option>
                    <option value="tiktok">TikTok</option>
                    <option value="discord">Discord</option>
                    <option value="soundcloud">SoundCloud</option>
                    <option value="bandcamp">Bandcamp</option>
                    <option value="facebook">Facebook</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Display Name:</label><input className="input-2006 w-full" value={socialForm.display_name} onChange={(e) => setSocialForm({...socialForm, display_name: e.target.value})} placeholder="e.g. YouTube" /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">URL:</label><input className="input-2006 w-full" value={socialForm.url} onChange={(e) => setSocialForm({...socialForm, url: e.target.value})} placeholder="https://..." /></div>
                <div className="mb-2"><label className="block text-[11px] text-[#ccc] mb-1">Order:</label><input type="number" className="input-2006 w-full" value={socialForm.order} onChange={(e) => setSocialForm({...socialForm, order: parseInt(e.target.value) || 0})} /></div>
                <div className="mb-2"><label className="flex items-center gap-2 text-[11px] text-[#ccc]"><input type="checkbox" checked={socialForm.is_active} onChange={(e) => setSocialForm({...socialForm, is_active: e.target.checked})} /> Active (visible on site)</label></div>
                <div className="flex gap-2 mt-4">
                  <button type="button" className="btn-2006 btn-2006-orange flex-1" onClick={handleSocialSubmit}>Save</button>
                  <button type="button" className="btn-2006 flex-1" onClick={() => setShowSocialModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="myspace-container max-w-sm w-full">
              <div className="myspace-header flex justify-between items-center">
                <h3>Confirm Delete</h3>
                <button type="button" onClick={() => setDeleteConfirm({ show: false, type: '', id: null, name: '' })}><X size={16} /></button>
              </div>
              <div className="p-4 bg-[#050d1a] text-center">
                <p className="text-[#ccc] mb-4">Are you sure you want to delete <span className="text-[#ff6600] font-bold">"{deleteConfirm.name}"</span>?</p>
                <p className="text-[#888] text-[11px] mb-4">This action cannot be undone.</p>
                <div className="flex gap-2">
                  <button type="button" className="btn-2006 btn-2006-orange flex-1" onClick={executeDelete}>Yes, Delete</button>
                  <button type="button" className="btn-2006 flex-1" onClick={() => setDeleteConfirm({ show: false, type: '', id: null, name: '' })}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-[10px] text-[#666] py-3 border-t border-[#1a4a7a]">
          <a href="/" className="text-[#66ccff]">&laquo; Back to Home</a>
        </div>
      </div>
    </div>
  );
}
